import "zod/compile";
import { z } from "zod";

/**
 * Gently-native supply network vocabulary. A customer's structure (Rexel's
 * hubs, branches and distribution centres) is one configuration of these
 * concepts, not the concepts themselves.
 *
 * A network version stores a {@link SupplyNetworkDocument}: only what the
 * network owns, including its own tree of location groups. Location names,
 * codes and divisions are master data, joined in when the document is read
 * (see {@link SupplyNetwork}). Divisions are only shown: the network's rules
 * run on its groups.
 */

export const SUPPLY_NETWORK_ROLE_ICONS = [
  "warehouse",
  "buildings",
  "storefront",
] as const;
export type RoleIcon = (typeof SUPPLY_NETWORK_ROLE_ICONS)[number];

/**
 * How much stock a location gives up to other locations, from most to least
 * protective. `all_available` keeps nothing back beyond what is already
 * promised (on hand minus reserved and already staged).
 */
export const STOCK_RELEASE_VALUES = [
  "above_max",
  "above_min",
  "all_available",
] as const;
export type StockRelease = (typeof STOCK_RELEASE_VALUES)[number];

/** Routine restocking never empties the giver's shelf. */
export const RESTOCK_RELEASE_VALUES = ["above_max", "above_min"] as const;
export type RestockRelease = (typeof RESTOCK_RELEASE_VALUES)[number];

/**
 * What stock floors are measured against: the GAINS min and max, or the
 * forecast reorder point (for "above min") and order-up-to level (for "above
 * max"), with the GAINS levels where an item has no forecast.
 */
export const FLOOR_LEVELS = ["gains_min_max", "forecast"] as const;
export type FloorLevels = (typeof FLOOR_LEVELS)[number];

/** Whether a transfer into this location is staged without a buyer. */
export const INBOUND_TRANSFER_MODES = ["auto_stage", "review"] as const;
export type InboundTransferMode = (typeof INBOUND_TRANSFER_MODES)[number];

/**
 * When auto release runs (GEN-3027): at the end of every replenishment engine
 * run, or only when the "Release Auto Transfers" step runs, so an
 * organization picks the time by scheduling that step's workflow.
 */
export const AUTO_RELEASE_TRIGGERS = ["engine_run", "schedule"] as const;
export type AutoReleaseTrigger = (typeof AUTO_RELEASE_TRIGGERS)[number];

/**
 * What happens to a line a source's daily line cap left for a later day:
 * ranked again with that day's new lines, or released before any of them.
 */
export const AUTO_RELEASE_CARRY_OVER = ["rerank", "queued_first"] as const;
export type AutoReleaseCarryOver = (typeof AUTO_RELEASE_CARRY_OVER)[number];

/** Most transfer lines one source may auto release in a day. */
export const AutoReleaseLineCapSchema = z.number().int().min(1).max(1_000_000);

/** How auto-staged transfers are released. Absent means each engine run, re-ranked. */
export const TransferReleaseSettingsSchema = z.strictObject({
  trigger: z.enum(AUTO_RELEASE_TRIGGERS),
  carryOver: z.enum(AUTO_RELEASE_CARRY_OVER),
});
export type TransferReleaseSettings = z.infer<
  typeof TransferReleaseSettingsSchema
>;

export const DEFAULT_TRANSFER_RELEASE: TransferReleaseSettings = {
  trigger: "engine_run",
  carryOver: "rerank",
};

export const ITEM_SOURCE_MODES = ["pin_source", "prefer_buy"] as const;
export type ItemSourceMode = (typeof ITEM_SOURCE_MODES)[number];

const IdSchema = z.string().trim().min(1).max(200);

export const StockReleasePolicySchema = z.strictObject({
  /** Routine top-ups of another location. */
  restock: z.enum(RESTOCK_RELEASE_VALUES),
  /**
   * When the asking location has customer orders it can't fill from its stock
   * plus what is on order. Never more protective than `restock`.
   */
  customerWaiting: z.enum(STOCK_RELEASE_VALUES),
});
export type StockReleasePolicy = z.infer<typeof StockReleasePolicySchema>;

/**
 * Suppliers and departments (item groups) a location that buys from vendors
 * still doesn't buy, such as a redistribution vendor every branch gets
 * through its hub. Their items come by transfer from the location's preferred
 * lane that carries them.
 */
export const BuyingExceptionsSchema = z.strictObject({
  supplierIds: z.array(IdSchema).max(500),
  itemGroupIds: z.array(IdSchema).max(500),
});
export type BuyingExceptions = z.infer<typeof BuyingExceptionsSchema>;

/** An org-defined bundle of replenishment behaviors. */
export const LocationRoleSchema = z.strictObject({
  id: IdSchema,
  /** Org-defined label, e.g. "Hub". Customer data, not product copy. */
  name: z.string().trim().min(1).max(80),
  icon: z.enum(SUPPLY_NETWORK_ROLE_ICONS),
  buysFromVendors: z.boolean(),
  /** What it doesn't buy even though it buys from vendors. Absent means none. */
  doesNotBuy: BuyingExceptionsSchema.optional(),
  releasesStock: StockReleasePolicySchema,
  inboundTransfers: z.enum(INBOUND_TRANSFER_MODES),
  /**
   * Most transfer lines a location with this role ships by auto release in a
   * day, as the source. Absent means no cap.
   */
  autoReleaseLineCap: AutoReleaseLineCapSchema.optional(),
});
export type LocationRole = z.infer<typeof LocationRoleSchema>;

/** The settings a role sets that a single location can override. */
export type RoleBehaviors = Pick<
  LocationRole,
  | "buysFromVendors"
  | "doesNotBuy"
  | "releasesStock"
  | "inboundTransfers"
  | "autoReleaseLineCap"
>;

/**
 * Settings one location pins for itself. Unset means "follow the role"; a set
 * value stays pinned even when it equals the role's, so a later change to the
 * role does not move this location.
 */
export const LocationOverridesSchema = z.strictObject({
  buysFromVendors: z.boolean().optional(),
  /** Replaces the role's exceptions; empty lists mean "buys everything". */
  doesNotBuy: BuyingExceptionsSchema.optional(),
  restock: z.enum(RESTOCK_RELEASE_VALUES).optional(),
  customerWaiting: z.enum(STOCK_RELEASE_VALUES).optional(),
  inboundTransfers: z.enum(INBOUND_TRANSFER_MODES).optional(),
  /** A pinned daily auto-release line cap; null pins "no cap". */
  autoReleaseLineCap: AutoReleaseLineCapSchema.nullable().optional(),
});
export type LocationOverrides = z.infer<typeof LocationOverridesSchema>;

export const GROUP_COLORS = [
  "violet",
  "sky",
  "emerald",
  "amber",
  "rose",
  "slate",
] as const;
export type GroupColor = (typeof GROUP_COLORS)[number];

/**
 * A named set of locations and other groups, owned by the network. Groups form
 * a tree: a group without a parent is a top-level group.
 */
export const SupplyNetworkGroupSchema = z.strictObject({
  id: IdSchema,
  name: z.string().trim().min(1).max(80),
  parentId: IdSchema.nullable(),
  color: z.enum(GROUP_COLORS),
  /**
   * Overrides the sibling rule's default for this group's locations, and for
   * subgroups that don't set their own.
   */
  siblingTransfers: z.enum(["on", "off"]).optional(),
});
export type SupplyNetworkGroup = z.infer<typeof SupplyNetworkGroupSchema>;

/** What the network stores about one location; the rest is master data. */
export const LocationSettingsSchema = z.strictObject({
  /** The location's id in master data (a warehouse id). */
  id: IdSchema,
  /** Null while the location has no role yet. */
  roleId: IdSchema.nullable(),
  /** The group it sits in. Absent while it is in no group. */
  groupId: IdSchema.nullable().optional(),
  /** Behaviors this location pins for itself, over its role's. */
  overrides: LocationOverridesSchema.optional(),
});
export type LocationSettings = z.infer<typeof LocationSettingsSchema>;

export const LANE_PRODUCT_FILTER_MODES = ["include", "exclude"] as const;
export type LaneProductFilterMode = (typeof LANE_PRODUCT_FILTER_MODES)[number];

/**
 * Which items a lane carries. An item matches when it is in one of the item
 * groups, or a listed supplier sells it at either end of the lane. "include"
 * carries only matching items; "exclude" carries every other item.
 */
export const LaneProductFilterSchema = z
  .strictObject({
    mode: z.enum(LANE_PRODUCT_FILTER_MODES),
    // Database ids: checked as UUIDs here, so a malformed one is a validation
    // problem rather than a failed lookup.
    itemGroupIds: z.array(z.uuid()).max(500),
    supplierIds: z.array(z.uuid()).max(500),
  })
  .refine(
    (filter) => filter.itemGroupIds.length + filter.supplierIds.length > 0,
    { message: "A product filter needs at least one item group or supplier." },
  );
export type LaneProductFilter = z.infer<typeof LaneProductFilterSchema>;

/** The highest priority a lane or the sibling rule can have. */
export const MAX_SUPPLY_NETWORK_PRIORITY = 10_000;

/** A one-way "from can ship to to" link. Lower priority is preferred. */
export const SupplyNetworkLaneSchema = z.strictObject({
  id: IdSchema,
  fromId: IdSchema,
  toId: IdSchema,
  priority: z.number().int().min(0).max(MAX_SUPPLY_NETWORK_PRIORITY),
  /** Overrides the lane defaults for this lane only. */
  transitDays: z.number().int().min(0).max(365).optional(),
  costPerTransfer: z.number().min(0).max(1_000_000).optional(),
  /** Without one, the lane carries every item. */
  productFilter: LaneProductFilterSchema.optional(),
});
export type SupplyNetworkLane = z.infer<typeof SupplyNetworkLaneSchema>;

/**
 * Lets locations fed by the same upstream transfer to each other, without
 * storing a lane for every pair.
 */
export const SiblingRuleSchema = z.strictObject({
  enabled: z.boolean(),
  /** Priority given to the lanes it generates. */
  priority: z.number().int().min(0).max(MAX_SUPPLY_NETWORK_PRIORITY),
  /** Only siblings in the same group share stock. */
  sameGroupOnly: z.boolean(),
});
export type SiblingRule = z.infer<typeof SiblingRuleSchema>;

/** Org-wide facts about an internal transfer. */
export const LaneDefaultsSchema = z.strictObject({
  transitDays: z.number().int().min(0).max(365),
  costPerTransfer: z.number().min(0).max(1_000_000),
});
export type LaneDefaults = z.infer<typeof LaneDefaultsSchema>;

/** A per item × location override layered on top of the lanes. */
export const ItemRuleSettingsSchema = z.strictObject({
  id: IdSchema,
  /** The item's SKU (its product v3 key). */
  sku: z.string().trim().min(1).max(200),
  locationId: IdSchema,
  mode: z.enum(ITEM_SOURCE_MODES),
  sourceLocationId: IdSchema.nullable(),
  /**
   * For a buy preference, the supplier to buy from. Without one the location
   * buys from the cheapest vendor it has for the item.
   */
  vendorId: IdSchema.nullable().optional(),
  /**
   * Set on rules the one-time GAINS import wrote. Editing a rule makes it the
   * editor's, so the mark goes.
   */
  importedFromGains: z.boolean().optional(),
});
export type ItemRuleSettings = z.infer<typeof ItemRuleSettingsSchema>;

export const SUPPLY_NETWORK_DOCUMENT_VERSION = 2;

/** One network version as stored: everything the network owns. */
export const SupplyNetworkDocumentSchema = z.strictObject({
  schemaVersion: z.literal(SUPPLY_NETWORK_DOCUMENT_VERSION),
  roles: z.array(LocationRoleSchema).max(50),
  /** System-wide behaviors for locations without a role. */
  defaultRole: LocationRoleSchema,
  groups: z.array(SupplyNetworkGroupSchema).max(1_000),
  locations: z.array(LocationSettingsSchema).max(5_000),
  lanes: z.array(SupplyNetworkLaneSchema).max(50_000),
  siblingRule: SiblingRuleSchema,
  laneDefaults: LaneDefaultsSchema,
  itemRules: z.array(ItemRuleSettingsSchema).max(500_000),
  /** Absent means the GAINS min and max. */
  floorLevels: z.enum(FLOOR_LEVELS).optional(),
  /** Absent means {@link DEFAULT_TRANSFER_RELEASE}. */
  transferRelease: TransferReleaseSettingsSchema.optional(),
});
export type SupplyNetworkDocument = z.infer<typeof SupplyNetworkDocumentSchema>;

const LegacyLocationRoleSchema = LocationRoleSchema.extend({
  suppliesEveryDivision: z.boolean(),
});

/**
 * Version 1, before groups: the rules ran on divisions, with per-division
 * sibling settings and a role flag for locations that supply every division.
 * Still readable; it is upgraded with master data when read.
 */
export const SupplyNetworkDocumentV1Schema = z.strictObject({
  schemaVersion: z.literal(1),
  roles: z.array(LegacyLocationRoleSchema).max(50),
  defaultRole: LegacyLocationRoleSchema,
  /** Keyed by division id. */
  divisionSettings: z.record(
    z.string().min(1),
    z.strictObject({ siblingTransfers: z.enum(["on", "off"]).optional() }),
  ),
  locations: z.array(LocationSettingsSchema).max(5_000),
  lanes: z.array(SupplyNetworkLaneSchema).max(50_000),
  siblingRule: SiblingRuleSchema.omit({ sameGroupOnly: true }).extend({
    sameDivisionOnly: z.boolean(),
  }),
  laneDefaults: LaneDefaultsSchema,
  itemRules: z.array(ItemRuleSettingsSchema).max(500_000),
  floorLevels: z.enum(FLOOR_LEVELS).optional(),
});
export type SupplyNetworkDocumentV1 = z.infer<
  typeof SupplyNetworkDocumentV1Schema
>;

/** A document as it may be stored: the current version or an older one. */
export const StoredSupplyNetworkDocumentSchema = z.discriminatedUnion(
  "schemaVersion",
  [SupplyNetworkDocumentV1Schema, SupplyNetworkDocumentSchema],
);
export type StoredSupplyNetworkDocument = z.infer<
  typeof StoredSupplyNetworkDocumentSchema
>;

/**
 * A business unit from the organization's master data. Divisions are shared
 * with rebates and reports, so the network only reads them: it never creates,
 * renames or deletes one, nor moves a location between them.
 */
export type SupplyNetworkDivision = {
  id: string;
  name: string;
};

/** Anywhere stock sits: the network's settings plus its master data. */
export type SupplyNetworkLocation = LocationSettings & {
  code: string;
  name: string;
  /**
   * From the location's master data, only shown; null until it is mapped to
   * one. The network's rules use its group.
   */
  divisionId: string | null;
  city: string | null;
  region: string | null;
};

export type ItemSourceRule = ItemRuleSettings & {
  itemName: string;
};

/** A network version as read: the document joined with master data. */
export type SupplyNetwork = Omit<
  SupplyNetworkDocument,
  "schemaVersion" | "locations" | "itemRules"
> & {
  /** Master data, read-only here. */
  divisions: SupplyNetworkDivision[];
  locations: SupplyNetworkLocation[];
  itemRules: ItemSourceRule[];
};
